<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { outputKeys } from './graph';
import { eventLabel } from './eventLabel';
import {
  conditionField,
  conditionMessages,
  conditionRuleValue,
} from './conditions';
import en from './en.json';
import ru from './ru.json';
import { startMessages } from './startSchedule';

const props = defineProps({
  run: { type: Object, required: true },
  catalog: { type: Object, default: null },
});
const { t, te, locale } = useI18n({
  useScope: 'local',
  messages: {
    en: { ...en, ...conditionMessages.en, ...startMessages.en },
    ru: { ...ru, ...conditionMessages.ru, ...startMessages.ru },
  },
});
const route = useRoute();
const dateLabel = value =>
  value
    ? new Intl.DateTimeFormat(locale.value, {
        dateStyle: 'medium',
        timeStyle: 'medium',
      }).format(new Date(value))
    : '—';
const label = key => (key && te(key) ? t(key) : key || '—');
const nodeFor = id => props.run.nodes[id] || {};
const nodeLabel = id => {
  const node = nodeFor(id);
  return (
    node.label ||
    node.name ||
    (te(node.type || '') ? t(node.type) : t('runs.unknownNode'))
  );
};
const reason = value => {
  if (value === true || value === false)
    return t(value ? 'runs.conditionTrue' : 'runs.conditionFalse');
  if (!value) return '';
  if (te(`runs.reasons.${value}`)) return t(`runs.reasons.${value}`);
  if (/^telegram_[1-5][0-9]{2}$/.test(value))
    return t('runs.telegramError', { code: value.slice(-3) });
  if (te(value)) return t(value);
  return t('runs.unclassified');
};
const running = computed(() =>
  ['active', 'waiting'].includes(props.run.status)
);
const paused = computed(
  () => running.value && props.run.scenario_status !== 'active'
);
const historic = computed(
  () => props.run.trace.length > 0 && !props.run.trace.some(step => step.kind)
);
const deliveriesFor = id =>
  props.run.deliveries.filter(delivery => delivery.node === id);
const fieldLabel = field =>
  props.catalog?.fields?.find(item => item.name === field)?.label || field;
const deliveryStatus = value =>
  te(`runs.deliveryStatus.${value}`)
    ? t(`runs.deliveryStatus.${value}`)
    : t('runs.unknown');
const deliveryNodes = computed(() => [
  ...new Set(props.run.deliveries.map(item => item.node)),
]);
</script>

<template>
  <article class="max-w-3xl mx-auto break-words">
    <h3 class="m-0 text-base font-semibold">
      {{ run.contact.name || t('runs.contact', { id: run.contact.id }) }}
    </h3>
    <p class="mt-1 mb-3 text-sm text-n-slate-11">
      {{ run.contact.phone || '—' }} ·
      {{ t('runs.contact', { id: run.contact.id }) }}
    </p>
    <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs mb-4">
      <dt class="text-n-slate-11">{{ t('runs.statusLabel') }}</dt>
      <dd class="m-0">
        {{ label(`runs.status.${run.status}`)
        }}<span v-if="paused"> · {{ t('runs.paused') }}</span>
      </dd>
      <dt class="text-n-slate-11">{{ t('runs.version') }}</dt>
      <dd class="m-0">
        {{
          run.version
            ? t('versions.number', { number: run.version })
            : t('runs.versionUnknown')
        }}
      </dd>
      <dt class="text-n-slate-11">{{ t('event') }}</dt>
      <dd class="m-0">
        {{ eventLabel(run.event.type, t) }} ·
        {{
          t(
            run.event.patient_id
              ? 'startSchedule.patientRecord'
              : 'runs.appointment',
            { id: run.event.patient_id || run.event.appointment_id }
          )
        }}
      </dd>
      <dt class="text-n-slate-11">{{ t('runs.entered') }}</dt>
      <dd class="m-0">{{ dateLabel(run.created_at) }}</dd>
      <dt v-if="run.wait_until" class="text-n-slate-11">
        {{ t('runtime.waitUntil') }}
      </dt>
      <dd v-if="run.wait_until" class="m-0">{{ dateLabel(run.wait_until) }}</dd>
      <dt v-if="run.execution_error" class="text-n-slate-11">
        {{ t('runs.reason') }}
      </dt>
      <dd v-if="run.execution_error" class="m-0 text-n-ruby-11">
        {{ reason(run.execution_error) }}
      </dd>
    </dl>
    <p class="text-xs text-n-slate-11">{{ t('runs.snapshotHint') }}</p>
    <p v-if="historic" class="text-xs text-n-amber-11">
      {{ t('runs.legacyHint') }}
    </p>
    <h4 class="text-sm font-semibold pt-4 border-t border-n-weak">
      {{ t('runs.path') }}
    </h4>
    <ol class="list-none m-0 p-0 divide-y divide-n-weak">
      <li v-for="(step, index) in run.trace" :key="index" class="py-3">
        <div class="flex gap-3">
          <span class="text-xs text-n-slate-11 tabular-nums pt-0.5">{{
            index + 1
          }}</span>
          <div class="min-w-0 flex-1">
            <p class="m-0 text-sm font-medium">
              {{ step.node ? nodeLabel(step.node) : t('exit') }}
              <span class="text-xs text-n-slate-11 font-normal">{{
                step.node
              }}</span>
            </p>
            <p class="m-0 mt-1 text-sm">
              {{
                reason(step.result) ||
                (step.kind === 'finish'
                  ? t('runs.finished')
                  : t('runs.noResult'))
              }}
            </p>
            <p
              v-if="
                step.edge &&
                outputKeys(nodeFor(step.node).type).includes(step.edge)
              "
              class="m-0 mt-1 text-xs"
            >
              {{ t('runs.branch', { branch: t(step.edge) }) }}
            </p>
            <p class="m-0 mt-1 text-xs text-n-slate-11">
              {{ dateLabel(step.at)
              }}<span v-if="step.until">
                · {{ t('runtime.waitUntil') }} {{ dateLabel(step.until) }}</span
              >
            </p>
            <details v-if="run.nodes[step.node]" class="mt-2 text-xs">
              <summary
                class="cursor-pointer text-n-slate-11 hover:text-n-slate-12 transition-colors"
              >
                {{ t('runs.nodeSettings') }}
              </summary>
              <div class="pt-2 space-y-2">
                <template v-if="nodeFor(step.node).type === 'condition'">
                  <p
                    v-if="
                      nodeFor(step.node).definition?.context ===
                      'appointment_service'
                    "
                    class="m-0"
                  >
                    {{
                      t(
                        nodeFor(step.node).definition.collection_match ===
                          'none'
                          ? 'conditionEditor.noProduct'
                          : 'conditionEditor.anyProduct'
                      )
                    }}
                  </p>
                  <p class="m-0">
                    {{ label(nodeFor(step.node).definition?.match || 'all') }}
                  </p>
                  <p
                    v-for="(rule, i) in nodeFor(step.node).definition?.rules ||
                    []"
                    :key="i"
                    class="m-0"
                  >
                    {{
                      label(
                        rule.source === 'event' ? 'eventSource' : rule.source
                      )
                    }}
                    · {{ conditionField(catalog, rule).label || rule.field }}
                    {{ label(rule.op) }}
                    {{
                      ['present', 'blank', 'changed', 'today'].includes(rule.op)
                        ? ''
                        : conditionRuleValue(catalog, rule, t)
                    }}
                  </p>
                </template>
                <template v-else-if="nodeFor(step.node).type === 'wait'">
                  <p class="m-0">
                    {{ label(nodeFor(step.node).mode) }} ·
                    {{ nodeFor(step.node).amount }}
                    {{ label(nodeFor(step.node).unit) }}
                  </p>
                  <p v-if="nodeFor(step.node).field" class="m-0">
                    {{ label(nodeFor(step.node).direction) }} ·
                    {{ fieldLabel(nodeFor(step.node).field) }} ·
                    {{ nodeFor(step.node).timezone }}
                  </p>
                </template>
                <template
                  v-else-if="
                    ['message', 'availability'].includes(
                      nodeFor(step.node).type
                    )
                  "
                >
                  <p class="m-0">
                    {{ t('channel') }}: {{ nodeFor(step.node).channel }}
                  </p>
                  <p class="m-0">
                    {{
                      nodeFor(step.node).inbox_id
                        ? t('runs.sourceId', {
                            id: nodeFor(step.node).inbox_id,
                          })
                        : t('runtime.appointmentSource')
                    }}
                  </p>
                  <p v-if="nodeFor(step.node).template_id" class="m-0">
                    {{
                      t('runs.templateId', {
                        id: nodeFor(step.node).template_id,
                      })
                    }}
                    · {{ label(`goal_${nodeFor(step.node).success_on}`) }}
                  </p>
                </template>
              </div>
            </details>
          </div>
        </div>
      </li>
      <li v-if="running" class="py-3 text-sm">
        <p class="m-0 font-medium">
          {{ t('runs.current') }}: {{ nodeLabel(run.current_node) }}
        </p>
        <p v-if="paused" class="m-0 mt-1 text-n-amber-11">
          {{ t('runs.pauseHint') }}
        </p>
        <p v-else class="m-0 mt-1 text-n-slate-11">
          {{ t('runs.currentHint') }}
        </p>
      </li>
      <li
        v-if="!run.trace.length && !running"
        class="py-3 text-sm text-n-slate-11"
      >
        {{ t('runs.noTrace') }}
      </li>
    </ol>
    <h4 class="text-sm font-semibold pt-4 mt-5 border-t border-n-weak">
      {{ t('runs.messages') }}
    </h4>
    <p class="text-xs text-n-slate-11">{{ t('runs.deliveryHint') }}</p>
    <p v-if="!run.deliveries.length" class="text-sm text-n-slate-11">
      {{ t('runs.noDeliveries') }}
    </p>
    <section
      v-for="node in deliveryNodes"
      :key="node || 'unknown'"
      class="mt-4"
    >
      <h5 class="text-xs font-medium">
        {{ node ? nodeLabel(node) : t('runs.unknownNode') }} · {{ node }}
      </h5>
      <div
        v-for="delivery in deliveriesFor(node)"
        :key="delivery.id"
        class="py-3 border-t border-n-weak"
      >
        <p class="m-0 text-sm font-medium">
          {{ deliveryStatus(delivery.status) }} · {{ delivery.channel }} ·
          {{ delivery.source || t('runs.sourceUnknown') }}
        </p>
        <p class="m-0 mt-1 text-xs text-n-slate-11">
          {{ t('runs.deliveryId', { id: delivery.id }) }} ·
          {{ dateLabel(delivery.sent_at || delivery.created_at) }}
        </p>
        <p v-if="delivery.error" class="m-0 mt-1 text-xs text-n-ruby-11">
          {{ reason(delivery.error) }}
        </p>
        <p
          v-if="delivery.mirror_failed"
          class="m-0 mt-1 text-xs text-n-amber-11"
        >
          {{ t('runs.mirrorFailed') }}
        </p>
        <p v-if="delivery.response_received" class="m-0 mt-1 text-xs">
          {{ t('runs.responseReceived') }}
        </p>
        <details v-if="delivery.body" class="mt-2 text-xs">
          <summary
            class="cursor-pointer text-n-slate-11 hover:text-n-slate-12 transition-colors"
          >
            {{ t('runs.messageContent') }}
          </summary>
          <p class="mt-2 whitespace-pre-wrap text-sm">{{ delivery.body }}</p>
          <p v-if="delivery.buttons.length" class="text-xs text-n-slate-11">
            {{ t('runs.buttons') }}: {{ delivery.buttons.join(' · ') }}
          </p>
        </details>
        <a
          v-if="delivery.conversation_id && route.params.accountId"
          :href="`/app/accounts/${route.params.accountId}/conversations/${delivery.conversation_id}`"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-block mt-2 text-xs text-n-brand hover:underline"
          >{{ t('runs.openChat')
          }}<span
            class="i-lucide-external-link ms-1 inline-block size-3"
            aria-hidden="true"
        /></a>
      </div>
    </section>
  </article>
</template>
